/**
 * CANALES DE COMISIÓN DEL CONTRATO — qué canales se pactan y cuáles se muestran.
 *
 * SSOT de las decisiones PURAS (sin DOM) que el modal de contratos aplica al pintar y al guardar.
 * Vive en shared/ para que el modal las use en producción y los tests las importen LITERALES: mientras
 * el test replicaba estas reglas a mano, podía divergir de la implementación sin ponerse rojo.
 *
 * Reglas de negocio fijadas por el dueño (PR-4b de SPEC_comision-solo-por-canal):
 *   1. Los porcentajes son CONTABLES — determinan la liquidación — y viven en UNA sola fuente de
 *      verdad: el contrato financiero del socio. Los acuerdos técnicos VLT/B2B los consumen, no copian.
 *   2. La detección de capacidad vive FUERA de la UI (`capacidadesDerivadas`, backend). Acá solo se
 *      proyecta lo que el backend derivó de dispositivos y acuerdos.
 *   3. Desactivar una capacidad OCULTA el bloque pero NO borra lo pactado.
 */

/**
 * Canales que el modal sabe pactar, con la capacidad de `capacidadesDerivadas` que los habilita.
 * `wallet` no declara capacidad: es universal (todo nodo tiene wallet).
 * Los ids son los canales canónicos de canal-de-venta.js.
 *
 * `vlt` se habilita con `vltConfigurable` (hay una terminal, activa O NO), no con `vlt` (hay una ACTIVA):
 * la comisión se pacta ANTES de activar, y activar exige el pacto. Mirar `vlt` acá cerraba el círculo —
 * el campo solo aparecía con la terminal activa, y la terminal no podía activarse sin el campo.
 */
export const CANALES_DE_COMISION_DEL_MODAL = [
    { id: 'wallet', etiqueta: 'Wallet', capacidad: null },
    { id: 'ticket', etiqueta: 'Ticket', capacidad: 'tickets' },
    { id: 'vlt', etiqueta: 'VLT', capacidad: 'vltConfigurable' },
    { id: 'b2b', etiqueta: 'B2B', capacidad: 'b2b' },
];

/**
 * Etiqueta visible de un canal (Ticket / Wallet / VLT / B2B), la MISMA en el modal del contrato, la ficha
 * financiera, la cascada y el modal del corte. `null` si el canal no está en el SSOT: el caller decide
 * qué hacer con un id desconocido (escaparlo, porque es texto que vino de la DB), nunca se inventa acá.
 * @param {string} canal - id canónico del canal.
 * @returns {string|null}
 */
export function etiquetaDeCanalDeComision(canal) {
    const delSsot = CANALES_DE_COMISION_DEL_MODAL.find(c => c.id === canal);
    return delSsot ? delSsot.etiqueta : null;
}

/**
 * ¿Se muestra el bloque de este canal? Decisión pura: no toca el DOM.
 *
 * @param {{id: string, capacidad: string|null}} canal
 * @param {Object|null|undefined} capacidades - `capacidadesDerivadas` del nodo.
 * @param {boolean} esPromotor - el Promotor Digital es solo-enlace por sistema: únicamente wallet.
 * @returns {boolean}
 */
export function canalEsVisible(canal, capacidades, esPromotor) {
    if (esPromotor) return canal.id === 'wallet';
    if (!canal.capacidad) return true; // wallet
    // Sin el dato de capacidades: conservador para los canales históricos (no esconder algo que el nodo
    // quizá opera) y cerrado para los nuevos, que exigen habilitación explícita.
    if (!capacidades) return canal.capacidad === 'tickets';
    return !!capacidades[canal.capacidad];
}

/**
 * Arma `comision_por_canal` a partir de los valores del formulario.
 *
 * Un canal OCULTO se persiste igual con el valor que ya tenía: ocultar no es despactar. Saltearlo
 * borraba del JSON la comisión vigente al guardar cualquier otro campo del contrato — dar de baja un
 * terminal VLT no puede anular en silencio lo acordado. Para dejar de cobrar hay que vaciar el % a mano.
 *
 * @param {Object<string, {valor: string, base?: string, sinCap?: boolean}>} valoresPorCanal
 *   Lo leído de los inputs. Un canal ausente o con `valor` vacío no se pacta.
 * @param {'create'|'edit'} modo
 * @returns {{comision_por_canal: Object|null|undefined}}
 *   `null` en CREATE sin canales (el backend responde 400 explicando que hace falta al menos uno);
 *   `undefined` en UPDATE (clave ausente ⇒ el backend preserva lo vigente, no lo pisa).
 */
export function construirComisionPorCanal(valoresPorCanal, modo) {
    const mapa = {};
    for (const { id: canal } of CANALES_DE_COMISION_DEL_MODAL) {
        const entrada = valoresPorCanal[canal];
        const valor = ((entrada && entrada.valor) || '').trim();
        if (valor === '') continue;
        const porcentaje = parseFloat(valor);
        const base = (entrada && entrada.base) || 'ggr';
        mapa[canal] = entrada && entrada.sinCap ? { porcentaje, base, sin_cap: true } : { porcentaje, base };
    }
    if (Object.keys(mapa).length > 0) return { comision_por_canal: mapa };
    return { comision_por_canal: modo === 'create' ? null : undefined };
}

/**
 * Clave del backfill v89. NO es un canal de venta: el motor nunca reparte producción con este nombre.
 * Retirada de todos los contratos vigentes de ambas marcas (migración 2026-08-12). Se sigue filtrando
 * al mostrar porque las versiones históricas la conservan — son inmutables y no se reescriben.
 */
export const CANAL_COMISION_LEGACY_GENERAL = 'default';

/**
 * Qué comisiones MOSTRAR de un `comision_por_canal`: los canales pactados, nada más.
 *
 * `default` no se lista nunca. No es un canal — no existe producción `default`, así que pintarlo
 * inventa una comisión que el motor no calcula. Un contrato vigente ya no lo trae; si aparece (versión
 * histórica), es residuo del backfill y se ignora igual que lo ignora el motor.
 *
 * @param {Object|null|undefined} comisionPorCanal - el mapa ya parseado (no el TEXT crudo).
 * @returns {Array<{canal: string, porcentaje: number, base: string|undefined}>}
 *   Vacío si no hay canales que mostrar. El orden respeta el del mapa.
 */
export function comisionesVisiblesDelContrato(comisionPorCanal) {
    if (!comisionPorCanal || typeof comisionPorCanal !== 'object') return [];

    return Object.keys(comisionPorCanal)
        .filter(canal => canal !== CANAL_COMISION_LEGACY_GENERAL)
        .map(canal => ({
            canal,
            porcentaje: Number(comisionPorCanal[canal]?.porcentaje) || 0,
            base: comisionPorCanal[canal]?.base,
        }));
}
